import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import TermsAcceptBlock from "./TermsAcceptBlock";
import { toast } from "sonner";
import { Loader2, ShieldAlert } from "lucide-react";

const LABEL = {
  buyer: "Buyer",
  seller: "Seller",
  cam_vendor: "CAM Vendor",
};

/** Post-login gate: if the current user still has pending T&C (legacy /
 *  grandfathered / new version published), show a blocking modal that
 *  cannot be dismissed until they accept every pending role doc.
 *
 *  Privacy is bundled into whichever role T&C block is shown — the block
 *  includes a mandatory privacy checkbox and the backend logs both records
 *  when the request includes 'privacy' in checkboxes. */
export default function TermsGate() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);      // e.g. ["privacy","buyer"] or ["cam_vendor"]
  const [versions, setVersions] = useState({});
  const [accepts, setAccepts] = useState({});
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user || checked) return;
    if (user.role === "admin" || user.role === "employee") {
      setChecked(true);
      return;
    }
    api
      .get("/terms/status")
      .then((r) => {
        setPending(r.data.pending || []);
        setVersions(r.data.current_versions || {});
        setChecked(true);
      })
      .catch(() => setChecked(true));
  }, [user, checked]);

  // Identify the primary role T&C the user must sign. Privacy is folded into it.
  const roleRoles = useMemo(() => pending.filter((r) => r !== "privacy"), [pending]);
  const currentRole = roleRoles[0] || null;
  // If somehow only privacy is pending (edge case: staff who was granted a
  // regular role later), fall back to Buyer view so the privacy checkbox is
  // still surfaced.
  const roleForBlock = currentRole || (pending.length ? "buyer" : null);

  if (!user || pending.length === 0 || !roleForBlock) return null;

  const acceptOne = async () => {
    const info = accepts[roleForBlock];
    if (!info?.accepted) {
      toast.error("Please tick all required boxes to continue.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/terms/accept", {
        role: roleForBlock,
        version: versions[roleForBlock],
        checkboxes: info.checkboxes,
        event: "reacceptance",
      });
      toast.success(`Terms accepted for ${LABEL[roleForBlock]}.`);
      const r = await api.get("/terms/status");
      setPending(r.data.pending || []);
      setAccepts({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save acceptance — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-slate-950/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="terms-gate">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl my-8">
        <div className="p-6 border-b border-border bg-amber-50 rounded-t-lg">
          <div className="flex items-center gap-2 text-amber-900 mb-1">
            <ShieldAlert className="h-5 w-5" />
            <span className="text-xs font-mono uppercase tracking-wider">Action required</span>
          </div>
          <h2 className="font-display font-bold text-xl">Please review and accept our Terms & Conditions</h2>
          <p className="text-sm text-muted-foreground mt-1">
            To continue using Jeweltix, we need your consent to the {LABEL[roleForBlock]} Terms &amp; Conditions (v{versions[roleForBlock]}) and the Privacy Policy.
          </p>
        </div>
        <div className="p-6">
          <TermsAcceptBlock
            role={roleForBlock}
            onChange={(v) => setAccepts((s) => ({ ...s, [roleForBlock]: v }))}
          />
        </div>
        <div className="p-4 border-t border-border flex items-center justify-between gap-3 bg-slate-50 rounded-b-lg">
          <p className="text-xs text-muted-foreground">You may still browse public pages, but transactions are paused until you accept.</p>
          <button
            onClick={acceptOne}
            disabled={busy || !accepts[roleForBlock]?.accepted}
            data-testid="terms-gate-accept"
            className="bg-primary text-primary-foreground rounded-md px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Accept & continue
          </button>
        </div>
      </div>
    </div>
  );
}
